import { inject, Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { isNotNullOrUndefined } from '@ep/shared';

export interface ListPagination {
  first: number;
  rows: number;
}

@Injectable({
  providedIn: 'root',
})
export class ListPaginationService {
  private readonly router = inject(Router);

  static readonly PAGE_QUERY_PARAM = 'page';
  static readonly ROWS_QUERY_PARAM = 'rows';

  read(defaultRows: number): ListPagination {
    const queryParams = this.router.parseUrl(this.router.url).queryParamMap;
    const rows = this.parseCount(queryParams.get(ListPaginationService.ROWS_QUERY_PARAM)) ?? defaultRows;
    const page = this.parseCount(queryParams.get(ListPaginationService.PAGE_QUERY_PARAM)) ?? 1;

    return { first: (page - 1) * rows, rows };
  }

  queryParams(): Params {
    const queryParams = this.router.parseUrl(this.router.url).queryParams;

    return [ListPaginationService.PAGE_QUERY_PARAM, ListPaginationService.ROWS_QUERY_PARAM].reduce(
      (params: Params, name) =>
        isNotNullOrUndefined(queryParams[name]) ? { ...params, [name]: queryParams[name] } : params,
      {}
    );
  }

  write(pagination: ListPagination, defaultRows: number): void {
    const page = pagination.rows > 0 ? Math.floor(pagination.first / pagination.rows) + 1 : 1;
    const urlTree = this.router.parseUrl(this.router.url);
    const queryParams: Params = { ...urlTree.queryParams };

    delete queryParams[ListPaginationService.PAGE_QUERY_PARAM];
    delete queryParams[ListPaginationService.ROWS_QUERY_PARAM];

    if (page > 1) queryParams[ListPaginationService.PAGE_QUERY_PARAM] = page;
    if (pagination.rows !== defaultRows) queryParams[ListPaginationService.ROWS_QUERY_PARAM] = pagination.rows;

    urlTree.queryParams = queryParams;

    const url = this.router.serializeUrl(urlTree);
    if (url !== this.router.url) this.router.navigateByUrl(url, { replaceUrl: true });
  }

  private parseCount(value: string | null): number | null {
    const count = Number(value);

    return isNotNullOrUndefined(value) && Number.isInteger(count) && count > 0 ? count : null;
  }
}
